import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchCollections,
  deleteCollectionById,
  duplicateCollection,
  updateCollection,
  getCurrentUser,
  updateMasteryStage,
} from "../../api";
import { useAuth0 } from "@auth0/auth0-react";
import SessionSettingsModal from "../../components/SessionSettingsModal";
import EditCollectionModal from "../../components/EditCollectionModal";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import SortableGridItem, {
  gridCollisionDetection,
} from "../../components/SortableGridItem";
import CollectionCardGrid from "../../components/CollectionCardGrid";
import { collectionColorSchemes, DEFAULT_CARD_BACKGROUND } from "../../constants/colorSchemes";
import { useTheme } from "../../context/ThemeContext";
import { tourStepsYourCollections } from "./tourStepsYourCollections";
import GuidedTour from "../../components/GuidedTour";
import { getReadableDisplayTextColor } from "../../utils/themeColors";
import { categoryColors } from "../../constants/categoryColors";
import CategoryBadge from "../../components/CategoryBadge";
import { VisibilityStates } from "../../types/VisibilityStates";
import {
  CollectionStage,
  USER_MASTERY_STAGES,
  USER_MASTERY_STAGE_LABELS,
  UserMasteryStage,
  collectionMatchesUserMasteryStage,
  getCollectionStageLabel,
  normalizeCollectionStage,
  normalizeUserMasteryStage,
  readStoredMasteryStage,
  writeStoredMasteryStage,
} from "../../constants/collectionStages";
import { contrastingTextColor, lightenColor } from "../../utils/colorUtils";
import BubbleText from "../../components/BubbleText";
import { parseCollectionItems } from "../../utils/parseCollectionItems";

/**
 * @fileoverview User collections management component with drag-and-drop organization.
 * 
 * @component YourCollections
 * @description Manages user-created collections with sorting, filtering, and organization features.
 * Supports drag-and-drop reordering and collection management operations.
 * 
 * @typedef {Object} Collection
 * @property {number} collection_id - Unique identifier
 * @property {string} name - Collection name
 * @property {string} category - Collection category
 * @property {string} description - Collection items JSON
 * @property {string} created_at - Creation timestamp
 * 
 * Features:
 * - Collection organization by category
 * - Multiple sort options
 * - Drag-and-drop reordering
 * - Collection CRUD operations
 * - Search functionality
 * - Session management
 * - Theme-aware styling
 * - Guided tour integration
 * 
 * Sort Options:
 * - Custom order (drag-and-drop)
 * - Date created
 * - Alphabetical
 * - Usage count
 */

interface Collection {
  collection_id: number;
  name: string;
  description: string;
  created_at: string;
  category: keyof typeof categoryColors;
  stage?: string;
  user_id: number;
  creator_username: string;
  creator_display_name: string;
  items: Item[];
  type: string;
  status: string;
  is_public: boolean;
}

interface Item {
  name: string;
}

const getItemsCount = (description: string | undefined): number => {
  return parseCollectionItems(description).length;
};

const parseDescription = (
  description: string,
): {
  name: string;
  id?: number;
  svg?: string;
  svgs?: string[];
  count?: number;
  answer?: string;
}[] => {
  return parseCollectionItems(description);
};

declare global {
  interface Window {
    savePreferenceTimeout: ReturnType<typeof setTimeout> | null;
  }
}

const YourCollections: React.FC = () => {
  const [isEditModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [filteredCollections, setFilteredCollections] = useState<Collection[]>(
    [],
  );
  const [selectedCategory, setSelectedCategory] =
    useState<string>("All Collections");
  const [myMasteryStage, setMyMasteryStage] = useState<UserMasteryStage>(() =>
    readStoredMasteryStage(),
  );
  const [sortOption, setSortOption] = useState<string>(
    localStorage.getItem("sortPreference") || "date",
  );
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedCollection, setSelectedCollection] =
    useState<Collection | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { theme, adjustColorForColorblindness } = useTheme();
  const cardFrameColor = adjustColorForColorblindness(
    theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
  );
  const cardFrameTextColor = contrastingTextColor(cardFrameColor);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const categories = [
    "All Collections",
    "Math",
    "Language Arts",
    "Number Sense",
    "Science",
    "Nursing",
    "Picture Quiz",
    "Picture Words",
  ];
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Visibility states for the tour
  const [visibilityStates, setVisibilityStates] = useState<VisibilityStates>({
    isCollectionCardVisible: false,
    isStartCollectionButtonVisible: false,
    isEditCollectionButtonVisible: false,
    isDeleteCollectionButtonVisible: false,
    isDotCountTypeVisible: false,
    isMinDotsVisible: false,
    isMaxDotsVisible: false,
    isTypeSelectVisible: false,
    isItemCountVisible: false,
    isCollectionItemCountVisible: false,
    isDotColorVisible: false,
    isDotShapeVisible: false,
    isGenerateRandomSequenceButtonVisible: false,
    isFileUploadVisible: false,
    isClearButtonVisible: false,
    isGeneratedSequencePreviewVisible: false,
    isSearchInputVisible: false,
    isSortSelectVisible: false,
    isCollectionsGridVisible: false,
    isPreviewButtonVisible: false,
    isSaveButtonVisible: false,
    isItemPreviewVisible: false,
    isMathProblemVisible: false,
    isDotButtonVisible: false,
    isImageUploadVisible: false,
    isNameInputVisible: false,
    isAddNameButtonVisible: false,
    isSpinButtonVisible: false,
    isNamesListVisible: false,
    isCollectionNameVisible: false,
    isCategorySelectVisible: false,
    isStageSelectVisible: false,
    isPublicCheckboxVisible: false,
    isSubmitButtonVisible: false,
    isSessionSettingsModalVisible: false,
    isEditCollectionModalVisible: false,
    isDuplicateCollectionModalVisible: false,
    isCollectionPreviewModalVisible: false,
    isNextButtonVisible: false,
  });

  // Tour state management
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  // Generate tour steps based on visibility states
  const steps = tourStepsYourCollections(visibilityStates);

  const handleTourComplete = () => {
    setIsTourRunning(false);
  };

  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };

  // Ensure the tour only starts when triggered from the Navbar
  useEffect(() => {
    // You can add any logic here if needed to check if the tour should start
  }, []);

  useEffect(() => {
    const fetchAndSetCollections = async () => {
      try {
        setIsLoading(true);
        const [fetchedCollections, currentUser] = await Promise.all([
          fetchCollections(getAccessTokenSilently),
          getCurrentUser(getAccessTokenSilently).catch(() => null),
        ]);
        const userStage = normalizeUserMasteryStage(
          (currentUser as { mastery_stage?: string } | null)?.mastery_stage ??
            readStoredMasteryStage(),
        );
        setMyMasteryStage(userStage);
        writeStoredMasteryStage(userStage);

        const nonGameCollections = fetchedCollections.filter(
          (collection: Collection) =>
            !collection.type &&
            collection.category !== undefined &&
            categories.includes(collection.category),
        );
        setCollections(nonGameCollections);
        filterAndSortCollections(
          nonGameCollections,
          selectedCategory,
          sortOption,
          userStage,
        );
      } catch (error) {
        console.error("Error fetching collections:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetCollections();
  }, [selectedCategory, getAccessTokenSilently]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setEditModalOpen(false);
      }
    };

    if (isEditModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditModalOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchButtonClick = () => {
    filterAndSortCollections(
      collections,
      selectedCategory,
      sortOption,
      myMasteryStage,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchButtonClick();
    }
  };

  const filterAndSortCollections = (
    collections: Collection[],
    category: string,
    sortOption: string,
    stageFilter: UserMasteryStage = myMasteryStage,
  ) => {
    let filtered = collections;

    if (category !== "All Collections") {
      filtered = collections.filter(
        (collection) => collection.category === category,
      );
    }

    filtered = filtered.filter((collection) =>
      collectionMatchesUserMasteryStage(collection.stage, stageFilter),
    );

    if (searchQuery) {
      filtered = filtered.filter((collection) =>
        collection.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (sortOption === "date") {
      filtered.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } else if (sortOption === "alphabetical") {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOption === "itemCount") {
      filtered.sort((a, b) => getItemsCount(b.description) - getItemsCount(a.description));
    } else if (sortOption === "custom") {
      // No sorting for "custom", maintain the order based on drag-and-drop
    }

    setFilteredCollections(filtered);
  };

  const handleMyMasteryStageChange = async (nextStage: UserMasteryStage) => {
    setMyMasteryStage(nextStage);
    writeStoredMasteryStage(nextStage);
    filterAndSortCollections(
      collections,
      selectedCategory,
      sortOption,
      nextStage,
    );
    try {
      await updateMasteryStage(nextStage, getAccessTokenSilently);
    } catch (error) {
      console.error("Error saving mastery stage:", error);
    }
  };

  const handleSaveUpdatedItems = async (
    newItems: {
      name: string;
      id?: number;
      svg?: string;
      svgs?: string[];
      count?: number;
      answer?: string;
    }[],
    newCollectionName: string,
    isPublic: boolean,
    stage: CollectionStage,
  ) => {
    setIsLoading(true);
    try {
      if (selectedCollection) {
        const filteredItems = newItems
          .filter((item) => String(item.name).trim() !== "")
          .map((item) => ({
            name: String(item.name),
            id: item.id,
            svg: item.svg,
            ...(item.svgs && item.svgs.length > 1
              ? { svgs: item.svgs.slice(0, 2) }
              : {}),
            count: item.count,
            ...(item.answer?.trim() ? { answer: item.answer.trim() } : {}),
          }));

        const updatedDescription = JSON.stringify(filteredItems);

        const updatedCollection = await updateCollection(
          selectedCollection.collection_id,
          newCollectionName,
          updatedDescription,
          selectedCollection.category,
          isPublic,
          getAccessTokenSilently,
          stage,
        );

        setCollections((prevCollections) =>
          prevCollections.map((col) =>
            col.collection_id === selectedCollection.collection_id
              ? updatedCollection
              : col,
          ),
        );

        setFilteredCollections((prevFiltered) =>
          prevFiltered.map((col) =>
            col.collection_id === selectedCollection.collection_id
              ? updatedCollection
              : col,
          ),
        );

        setSelectedCollection(updatedCollection);
        setEditModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating collection:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartCollection = (collectionId: number) => {
    const collection = collections.find(
      (col) => col.collection_id === collectionId,
    );
    if (collection) {
      setSelectedCollection(collection);
      setShowModal(true);
    }
  };

  const handleEditButtonClick = (collection: Collection) => {
    setSelectedCollection(collection);
    setEditModalOpen(true);
  };

  const handleDuplicateConfirm = async (collection: Collection) => {
    try {
      setIsLoading(true);
      await duplicateCollection(collection, getAccessTokenSilently);

      const refreshedCollections = await fetchCollections(
        getAccessTokenSilently,
      );
      setCollections(refreshedCollections);
      filterAndSortCollections(
        refreshedCollections,
        selectedCategory,
        sortOption,
        myMasteryStage,
      );
    } catch (error) {
      console.error("Error duplicating collection:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCollection = async (collectionId: number) => {
    try {
      await deleteCollectionById(collectionId, getAccessTokenSilently);
      const updatedCollections = collections.filter(
        (collection) => collection.collection_id !== collectionId,
      );
      setCollections(updatedCollections);
      filterAndSortCollections(
        updatedCollections,
        selectedCategory,
        sortOption,
        myMasteryStage,
      );
      // Update visibility states based on the updated collections
      setVisibilityStates((prev) => ({
        ...prev,
        isCollectionCardVisible: updatedCollections.length > 0,
      }));
    } catch (error) {
      console.error("Error deleting collection:", error);
    }
  };

  const handleStartSession = (
    min: number,
    sec: number,
    shuffle: boolean,
    speed: number,
    textColor: string,
    answerDisplayTime: number,
    stopCondition: string,
    timerMinutes: number,
    timerSeconds: number,
    isInteractive: boolean = false,
  ) => {
    if (selectedCollection) {
      // Save the last used time settings
      localStorage.setItem("lastUsedMinutes", min.toString());
      localStorage.setItem("lastUsedSeconds", sec.toString());
      localStorage.setItem("lastUsedSpeed", speed.toString());

      const sequenceItems = parseCollectionItems(
        selectedCollection.description,
      );
      const sequence = sequenceItems.map(
        (item, index) => ({
          name: item.name,
          svg: item.svg,
          svgs: item.svgs,
          count: item.count,
          answer: item.answer,
          isAnswer:
            selectedCollection.type === "mathProblems" && index % 2 !== 0,
        }),
      );

      navigate("/fullscreen-display", {
        state: {
          sequence,
          speed,
          shuffle,
          category: selectedCollection.category,
          type: selectedCollection.type,
          answerDisplayTime,
          stopCondition,
          timerMinutes,
          timerSeconds,
          isInteractive,
          collectionId: selectedCollection.collection_id,
        },
      });
      setShowModal(false);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "Unknown Date";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour12: true,
      timeZone: "America/Denver",
    }).format(date);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || sortOption !== "custom") {
      return;
    }

    const oldIndex = filteredCollections.findIndex(
      (collection) => collection.collection_id.toString() === String(active.id),
    );
    const newIndex = filteredCollections.findIndex(
      (collection) => collection.collection_id.toString() === String(over.id),
    );
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const newFilteredCollections = arrayMove(
      filteredCollections,
      oldIndex,
      newIndex,
    );

    setFilteredCollections(newFilteredCollections);

    setCollections((prevCollections) => {
      const visibleIds = new Set(
        newFilteredCollections.map((col) => col.collection_id),
      );
      let nextVisibleIndex = 0;
      return prevCollections.map((col) =>
        visibleIds.has(col.collection_id)
          ? newFilteredCollections[nextVisibleIndex++]
          : col,
      );
    });

    if (window.savePreferenceTimeout) {
      clearTimeout(window.savePreferenceTimeout);
    }
    window.savePreferenceTimeout = setTimeout(() => {
      localStorage.setItem("sortPreference", "custom");
      window.savePreferenceTimeout = null;
    }, 500);
  };

  const adjustColorForTheme = useCallback(
    (color: string) => {
      return adjustColorForColorblindness(color);
    },
    [adjustColorForColorblindness],
  );

  useEffect(() => {
    setVisibilityStates((prevState) => ({
      ...prevState, // Preserve existing values
      isCollectionCardVisible: collections.length > 0,
      isStartCollectionButtonVisible: collections.length > 0,
      isEditCollectionButtonVisible: collections.length > 0,
      isDeleteCollectionButtonVisible: collections.length > 0,
      isSessionSettingsModalVisible: showModal,
      isEditCollectionModalVisible: isEditModalOpen,
    }));
  }, [collections, showModal, isEditModalOpen]);

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSortOption = event.target.value;
    setSortOption(newSortOption);
    localStorage.setItem("sortPreference", newSortOption);
    filterAndSortCollections(
      collections,
      selectedCategory,
      newSortOption,
      myMasteryStage,
    );
  };

  // Add this function to group collections by category
  const groupedCollections = filteredCollections.reduce(
    (acc, collection) => {
      const category = collection.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(collection);
      return acc;
    },
    {} as Record<string, Collection[]>,
  );

  return (
    <div
      className="your-collections-page page-container mt-4 text-theme-text md:mt-6"
    >
      <h1 className="inherit mb-8 text-3xl font-bold">
        <BubbleText>Your Collections</BubbleText>
      </h1>

      {/* Search Input */}
      <div className="mb-4 flex w-full flex-col items-stretch justify-center gap-2 sm:flex-row sm:items-center">
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          placeholder="Search collections"
          className="search-collections-input w-full rounded border bg-white p-2 text-black sm:max-w-md"
        />
        <button
          type="button"
          onClick={handleSearchButtonClick}
          className="min-h-[44px] rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600 sm:ml-2"
        >
          Search
        </button>
      </div>

      <div className="collections-filters mb-4 flex w-full flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4">
        {/* Categories Dropdown */}
        <div className="flex w-full flex-col items-center gap-1 sm:my-4 sm:w-auto">
          <label htmlFor="categorySelect" className="inherit text-center font-bold">
            Categories:
          </label>
          <select
            id="categorySelect"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              filterAndSortCollections(
                collections,
                e.target.value,
                sortOption,
                myMasteryStage,
              );
            }}
            className="min-h-[44px] w-full rounded border bg-white p-2 text-black sm:w-auto"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="flex w-full flex-col items-center gap-1 sm:my-4 sm:w-auto">
          <label htmlFor="myMasteryStageSelect" className="inherit text-center font-bold">
            My stage:
          </label>
          <select
            id="myMasteryStageSelect"
            value={myMasteryStage}
            onChange={(e) =>
              handleMyMasteryStageChange(
                normalizeUserMasteryStage(e.target.value),
              )
            }
            className="min-h-[44px] w-full rounded border bg-white p-2 text-black sm:w-auto"
          >
            {USER_MASTERY_STAGES.map((value) => (
              <option key={value} value={value}>
                {USER_MASTERY_STAGE_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        {/* Sort by Dropdown */}
        <div className="flex w-full flex-col items-center gap-1 sm:my-4 sm:w-auto">
          <label htmlFor="sortSelect" className="inherit text-center font-bold">
            Sort by:
          </label>
          <select
            id="sortSelect"
            value={sortOption}
            onChange={handleSortChange}
            className="min-h-[44px] w-full rounded border bg-white p-2 text-black sm:w-auto"
          >
            <option value="date">Date</option>
            <option value="alphabetical">Alphabetical</option>
            <option value="itemCount">Item Count</option>
            <option value="custom">Custom Order</option>
          </select>
        </div>
      </div>

      {myMasteryStage !== "all" &&
        filteredCollections.length === 0 &&
        collections.length > 0 && (
          <p className="inherit mb-6 text-center text-sm">
            No {USER_MASTERY_STAGE_LABELS[myMasteryStage]} collections yet. Tag
            decks with that stage when creating or editing, or choose Show all.
          </p>
        )}

      {sortOption === "custom" ? (
        <>
          <CollectionCardGrid className="mb-4">
            <div
              onClick={() => navigate("/new-collection")}
              className="collection-card flex min-w-0 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-4 border-dashed border-gray-300 p-4 transition-all duration-300 hover:border-blue-500"
              style={{
                backgroundColor: cardFrameColor,
                color: cardFrameTextColor,
              }}
            >
              <div className="mb-4 rounded-full border border-black bg-green-500 p-4">
                <svg
                  className="size-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </div>
              <h3 className="inherit text-xl font-bold">
                Create New Collection
              </h3>
              <p className="inherit mt-2 text-sm opacity-75">
                Click to add a new collection
              </p>
            </div>
          </CollectionCardGrid>
          <DndContext
            sensors={dndSensors}
            collisionDetection={gridCollisionDetection}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={filteredCollections.map((collection) =>
                collection.collection_id.toString(),
              )}
              strategy={rectSortingStrategy}
            >
              <div className="grid-container w-full">
                <CollectionCardGrid>
                  {filteredCollections.map((collection, index) => (
                    <SortableGridItem
                      key={collection.collection_id}
                      id={collection.collection_id.toString()}
                      className={`collection-card min-w-0 h-full w-full cursor-grab rounded-lg border-4 p-4 shadow-lg active:cursor-grabbing text-theme-text`}
                      style={{
                        backgroundColor: cardFrameColor,
                      }}
                    >
                      <CollectionContent
                        collection={collection}
                        baseColor={adjustColorForTheme(
                          collectionColorSchemes[
                            index % collectionColorSchemes.length
                          ].backgroundColor,
                        )}
                        handleStartCollection={handleStartCollection}
                        handleEditButtonClick={handleEditButtonClick}
                        handleDeleteCollection={handleDeleteCollection}
                        handleDuplicateConfirm={handleDuplicateConfirm}
                        formatDate={formatDate}
                        completionCount={0}
                        theme={theme}
                      />
                    </SortableGridItem>
                  ))}
                </CollectionCardGrid>
              </div>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        <div className="grid-container w-full">
          {Object.entries(groupedCollections).map(
            ([category, categoryCollections], categoryIndex) => (
              <div key={category} className="mb-8 w-full">
                <h2 className="inherit mb-4 text-xl font-bold">{category}</h2>
                <CollectionCardGrid>
                  {categoryIndex === 0 && (
                    <div
                      onClick={() => navigate("/new-collection")}
                      className="collection-card flex min-w-0 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-4 border-dashed border-gray-300 p-4 transition-all duration-300 hover:border-blue-500"
                      style={{
                        backgroundColor: cardFrameColor,
                        color: cardFrameTextColor,
                      }}
                    >
                      <div className="mb-4 rounded-full border border-black bg-green-500 p-4">
                        <svg
                          className="size-12 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </div>
                      <h3 className="inherit text-xl font-bold">
                        Create New Collection
                      </h3>
                      <p className="inherit mt-2 text-sm opacity-75">
                        Click to add a new collection
                      </p>
                    </div>
                  )}
                  {categoryCollections.map((collection, index) => (
                    <div
                      key={collection.collection_id}
                      className={`collection-card min-w-0 w-full rounded-lg border-4 p-4 shadow-lg text-theme-text`}
                      style={{
                        backgroundColor: cardFrameColor,
                      }}
                    >
                      <CollectionContent
                        collection={collection}
                        baseColor={adjustColorForTheme(
                          collectionColorSchemes[
                            index % collectionColorSchemes.length
                          ].backgroundColor,
                        )}
                        handleStartCollection={handleStartCollection}
                        handleEditButtonClick={handleEditButtonClick}
                        handleDeleteCollection={handleDeleteCollection}
                        handleDuplicateConfirm={handleDuplicateConfirm}
                        formatDate={formatDate}
                        completionCount={0}
                        theme={theme}
                      />
                    </div>
                  ))}
                </CollectionCardGrid>
              </div>
            ),
          )}
        </div>
      )}

      {showModal && selectedCollection && (
        <SessionSettingsModal
          collectionName={selectedCollection.name}
          onClose={() => setShowModal(false)}
          onStart={handleStartSession}
          currentSettings={{
            speed: parseInt(localStorage.getItem("lastUsedSpeed") || "500"),
            textColor: getReadableDisplayTextColor(theme),
          }}
          category={selectedCollection.category}
          type={selectedCollection.type || "mathProblems"}
        />
      )}
      {isEditModalOpen && selectedCollection && (
        <EditCollectionModal
          isOpen={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          collectionName={selectedCollection.name}
          items={parseDescription(selectedCollection.description)}
          onSave={handleSaveUpdatedItems}
          type={selectedCollection.type}
          category={selectedCollection.category}
          stage={selectedCollection.stage}
          isPublic={selectedCollection.status === "public"}
        />
      )}
      {isLoading && <div className="inherit">Loading...</div>}

      {/* Add the GuidedTour component here */}
      <GuidedTour
        steps={steps}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={handleTourStepChange}
        tourName="yourCollections"
      />
    </div>
  );
};

interface CollectionContentProps {
  collection: Collection;
  baseColor: string;
  handleStartCollection: (id: number) => void;
  handleEditButtonClick: (collection: Collection) => void;
  handleDeleteCollection: (id: number) => void;
  handleDuplicateConfirm: (collection: Collection) => void;
  formatDate: (dateString: string) => string;
  completionCount: number;
  theme: {
    backgroundColor: string;
    textColor: string;
    originalTextColor: string;
  };
}

const CollectionContent: React.FC<CollectionContentProps> = ({
  collection,
  baseColor,
  handleStartCollection,
  handleEditButtonClick,
  handleDeleteCollection,
  handleDuplicateConfirm,
  formatDate,
}) => {
  const itemCount = getItemsCount(collection.description);

  const ensureValidColor = (color: string) => {
    return color && color.startsWith("#") ? color : "#FFFFFF";
  };

  const getLightenedBackgroundColor = () => {
    const validBaseColor = ensureValidColor(baseColor);
    const lightened = lightenColor(validBaseColor, 0.7);
    return lightened;
  };

  const headerColor = ensureValidColor(baseColor);
  const contentColor = getLightenedBackgroundColor();

  return (
    <div className="flex size-full flex-col">
      <h1
        className="border-5 w-full rounded-t-lg border-b-0 border-black p-2.5 text-center text-xl font-bold"
        style={{
          backgroundColor: headerColor,
          color: contrastingTextColor(headerColor),
          transition: "background-color 0.3s ease",
        }}
      >
        {collection.name}
      </h1>
      <div
        className="border-5 flex size-full flex-col rounded-b-lg border-black"
        style={{
          backgroundColor: contentColor,
          transition: "background-color 0.3s ease",
        }}
      >
        {/* Content wrapper with padding */}
        <div className="flex size-full flex-col p-4">
          {/* Info section - added text-center to center all text */}
          <div className="mb-auto text-center">
            <p className="mb-1 text-base font-bold text-black">
              {itemCount} {itemCount === 1 ? "item" : "items"} in collection
            </p>
            <p className={`mb-1 text-sm text-black`}>
              Category: <CategoryBadge category={collection.category} />
            </p>
            {normalizeCollectionStage(collection.stage) !== "none" && (
              <p className="mb-1 text-sm text-black">
                Stage:{" "}
                <span className="inline-block rounded bg-teal-700 px-2 py-0.5 text-xs font-semibold text-white">
                  {getCollectionStageLabel(collection.stage)}
                </span>
              </p>
            )}
            <p className="mb-2.5 text-base font-bold text-black">
              Created by you on {formatDate(collection.created_at)}
            </p>
          </div>

          {/* Buttons section */}
          <div className="mt-auto pt-4">
            <div className="mb-2.5 flex justify-center">
              <button
                type="button"
                className="start-collection-button w-2/3 cursor-pointer rounded-lg border-4 border-black bg-green-600 p-2 text-base font-bold text-black transition-all duration-300 hover:scale-105 hover:opacity-80 active:scale-95"
                onClick={() => handleStartCollection(collection.collection_id)}
              >
                Start
              </button>
            </div>
            <div className="flex w-full justify-between space-x-4">
              <button
                type="button"
                className="edit-collection-button flex-1 cursor-pointer rounded-lg border-4 border-black bg-yellow-400 p-2 text-base font-bold text-black transition-all duration-300 hover:scale-105 hover:opacity-80 active:scale-95"
                onClick={() => handleEditButtonClick(collection)}
              >
                Edit
              </button>
              <button
                type="button"
                className="duplicate-collection-button flex-1 cursor-pointer rounded-lg border-4 border-black bg-blue-500 p-2 text-base font-bold text-black transition-all duration-300 hover:scale-105 hover:opacity-80 active:scale-95"
                onClick={() => handleDuplicateConfirm(collection)}
              >
                Duplicate
              </button>
              <button
                type="button"
                className="delete-collection-button flex-1 cursor-pointer rounded-lg border-4 border-black bg-red-600 p-2 text-base font-bold text-black transition-all duration-300 hover:scale-105 hover:opacity-80 active:scale-95"
                onClick={() => handleDeleteCollection(collection.collection_id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YourCollections;
